import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CopyButton } from '@/components/CopyButton';
import { MIN_REASON_LENGTH } from '@/components/ResetPasswordDialog';
import { StatusBanner } from '@/components/StatusBanner';
import { fetchPlans } from '@/lib/adminCredits';
import { parseAnswersText } from '@/lib/onboardingAnswers';
import {
  createOnboardingAccount,
  deleteOnboardingTemplate,
  fetchOnboardingTemplates,
  saveOnboardingTemplate,
  type OnboardingAccountResult,
  type OnboardingTemplate,
} from '@/lib/onboardingTestApi';

const TEMPLATES_KEY = ['admin', 'onboarding-test', 'templates'] as const;
const PLANS_KEY = ['admin', 'plans'] as const;
const CUSTOM = '';
const NONE_ID = 'builtin:none';

const FIELD =
  'mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus disabled:opacity-50';
const LABEL = 'mt-4 block text-meta font-medium text-fg-secondary';
const BTN_SECONDARY =
  'rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50';
const BTN_PRIMARY =
  'rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function prettyAnswers(t: OnboardingTemplate): string {
  return t.answers ? JSON.stringify(t.answers, null, 2) : '';
}

function ReadonlyRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const id = `onboarding-result-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="mt-3">
      <label className="block text-meta font-medium text-fg-secondary" htmlFor={id}>
        {label}
      </label>
      <div className="mt-1 flex gap-2">
        <input
          id={id}
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className={`min-w-0 flex-1 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary ${mono ? 'font-mono' : ''}`}
        />
        <CopyButton text={value} label={label.toLowerCase()} />
      </div>
    </div>
  );
}

function ResultView({
  result,
  onAnother,
  onClose,
}: {
  result: OnboardingAccountResult;
  onAnother: () => void;
  onClose: () => void;
}) {
  const { quiz } = result;
  return (
    <>
      <h2 className="text-h3 font-semibold text-fg-primary">Test account ready</h2>
      <ReadonlyRow label="Sign-in link" value={result.login_url} mono />
      <p className="mt-2 text-meta">
        <a
          href={result.login_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-fg-primary underline"
        >
          Open in new window
        </a>
      </p>
      <ReadonlyRow label="Email" value={result.email} />
      <ReadonlyRow label="Password" value={result.password} mono />
      <p className="mt-3 text-body text-fg-secondary">Plan: {result.plan.name}</p>
      <div className="mt-3">
        {quiz.written ? (
          <StatusBanner kind="success" message={`Quiz answers written (${quiz.answers_count})`} />
        ) : quiz.error ? (
          <StatusBanner kind="error" message={`Quiz answers were not written: ${quiz.error}`} />
        ) : (
          <p className="text-body text-fg-secondary">No quiz document</p>
        )}
      </div>
      <p className="mt-3 text-meta text-fg-hint">
        The account is single-use: finishing or skipping the onboarding ends it. The link signs in
        whoever opens it, so treat it like a password.
      </p>
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onClose} className={BTN_SECONDARY}>
          Close
        </button>
        <button type="button" onClick={onAnother} className={BTN_PRIMARY}>
          Generate another
        </button>
      </div>
    </>
  );
}

export function OnboardingTestAccountDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<string>(CUSTOM);
  const [text, setText] = useState('');
  const [segment, setSegment] = useState('');
  const [planId, setPlanId] = useState('');
  const [reason, setReason] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [notice, setNotice] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState<OnboardingAccountResult | null>(null);

  const templatesQuery = useQuery({ queryKey: TEMPLATES_KEY, queryFn: fetchOnboardingTemplates });
  const plansQuery = useQuery({ queryKey: PLANS_KEY, queryFn: fetchPlans });
  const templates = templatesQuery.data ?? [];
  const builtins = templates.filter((t) => t.builtin);
  const saved = templates.filter((t) => !t.builtin);
  const selected = templates.find((t) => t.id === templateId);
  const paidPlans = (plansQuery.data ?? []).filter((p) => !p.is_default);

  const parsed = parseAnswersText(text);
  const trimmedReason = reason.trim();
  const reasonOk = trimmedReason.length >= MIN_REASON_LENGTH;

  const generate = useMutation({
    mutationFn: () =>
      createOnboardingAccount({
        answers: parsed.answers,
        segment: segment.trim(),
        plan_id: planId || null,
        reason: trimmedReason,
      }),
    onMutate: () => setNotice(null),
    onSuccess: (data) => setResult(data),
    onError: (err) => setNotice({ kind: 'error', message: errorMessage(err, 'Could not generate the account.') }),
  });

  const save = useMutation({
    mutationFn: () =>
      saveOnboardingTemplate({
        name: templateName.trim(),
        segment: segment.trim(),
        answers: parsed.answers!,
      }),
    onMutate: () => setNotice(null),
    onSuccess: async (template) => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      setTemplateId(template.id);
      setNotice({ kind: 'success', message: `Template "${template.name}" saved.` });
    },
    onError: (err) => setNotice({ kind: 'error', message: errorMessage(err, 'Could not save the template.') }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteOnboardingTemplate(id),
    onMutate: () => setNotice(null),
    onSuccess: async () => {
      setConfirmDelete(false);
      setTemplateId(CUSTOM);
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      setNotice({ kind: 'success', message: 'Template deleted.' });
    },
    onError: (err) => {
      setConfirmDelete(false);
      setNotice({ kind: 'error', message: errorMessage(err, 'Could not delete the template.') });
    },
  });

  const busy = generate.isPending || save.isPending || remove.isPending;
  const canGenerate = !parsed.error && reasonOk && !busy;
  const canSave = !parsed.error && parsed.answers !== null && templateName.trim().length > 0 && !busy;

  function chooseTemplate(id: string) {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setText(prettyAnswers(t));
    setSegment(t.segment);
    if (!t.builtin) setTemplateName(t.name);
  }

  function editText(next: string) {
    setText(next);
    // Typing into the "none" template makes it a custom set of answers.
    if (templateId === NONE_ID) setTemplateId(CUSTOM);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding test account"
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
    >
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-panel border border-border bg-surface-field p-6">
        {result ? (
          <ResultView
            result={result}
            onAnother={() => {
              setResult(null);
              setNotice(null);
              generate.reset();
            }}
            onClose={onClose}
          />
        ) : (
          <>
            <h2 className="text-h3 font-semibold text-fg-primary">Onboarding test account</h2>
            <p className="mt-2 text-body text-fg-secondary">
              Creates a throwaway quiz-funnel buyer on dev to try the first-run onboarding.
            </p>

            <label className={LABEL} htmlFor="onboarding-template">
              Template
            </label>
            <select
              id="onboarding-template"
              value={templateId}
              onChange={(e) => chooseTemplate(e.target.value)}
              disabled={busy}
              className={FIELD}
            >
              <option value={CUSTOM}>Custom</option>
              {builtins.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
              {saved.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.updated_by ? ` (by ${t.updated_by})` : ''}
                </option>
              ))}
            </select>
            {templatesQuery.isError && (
              <p className="mt-1 text-meta text-destructive">Could not load templates.</p>
            )}

            <label className={LABEL} htmlFor="onboarding-answers">
              Quiz answers (JSON)
            </label>
            <textarea
              id="onboarding-answers"
              value={text}
              onChange={(e) => editText(e.target.value)}
              rows={10}
              spellCheck={false}
              disabled={busy}
              aria-invalid={parsed.error ? true : undefined}
              className={`${FIELD} font-mono`}
            />
            {parsed.error && (
              <p role="alert" className="mt-1 text-meta text-destructive">
                {parsed.error}
              </p>
            )}
            {templateId === NONE_ID && !parsed.error && (
              <p className="mt-1 text-meta text-fg-hint">
                No quiz document: the onboarding is built from defaults, with no recap.
              </p>
            )}

            <label className={LABEL} htmlFor="onboarding-segment">
              Segment
            </label>
            <input
              id="onboarding-segment"
              type="text"
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              disabled={busy}
              className={FIELD}
            />
            <p className="mt-1 text-meta text-fg-hint">The funnel uses A1, A2, B, C.</p>

            <label className={LABEL} htmlFor="onboarding-template-name">
              Save as template
            </label>
            <div className="mt-1 flex flex-wrap gap-2">
              <input
                id="onboarding-template-name"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name (shared with all admins)"
                disabled={busy}
                className="min-w-0 flex-1 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary placeholder:text-fg-placeholder focus-visible:border-border-focus"
              />
              <button type="button" onClick={() => save.mutate()} disabled={!canSave} className={BTN_SECONDARY}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              {selected && !selected.builtin && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  className={BTN_SECONDARY}
                >
                  Delete
                </button>
              )}
            </div>

            <label className={LABEL} htmlFor="onboarding-plan">
              Plan
            </label>
            <select
              id="onboarding-plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              disabled={busy}
              className={FIELD}
            >
              <option value="">Default paid plan</option>
              {paidPlans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <label className={LABEL} htmlFor="onboarding-reason">
              Reason (required, at least {MIN_REASON_LENGTH} characters)
            </label>
            <textarea
              id="onboarding-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              disabled={busy}
              className={FIELD}
            />

            {notice && (
              <div className="mt-3">
                <StatusBanner kind={notice.kind} message={notice.message} />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={onClose} disabled={busy} className={BTN_SECONDARY}>
                Cancel
              </button>
              <button type="button" onClick={() => generate.mutate()} disabled={!canGenerate} className={BTN_PRIMARY}>
                {generate.isPending ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </>
        )}
      </div>
      {confirmDelete && selected && (
        <ConfirmDialog
          title="Delete template"
          description={`Delete "${selected.name}" for all admins? This cannot be undone.`}
          confirmLabel="Delete"
          busy={remove.isPending}
          onConfirm={() => remove.mutate(selected.id)}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
